import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactGA from 'react-ga4';
import Clarity from '@microsoft/clarity';
import { apiService } from "../services/api";
import html2canvas from 'html2canvas';
import { saveToHistory } from '../utils/analysisHistory';

const HomePage = ({ onPageChange, selectedVideoId, onAnalysisComplete }) => {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisStage, setAnalysisStage] = useState("");
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showMetricPopup, setShowMetricPopup] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [analysisLanguage, setAnalysisLanguage] = useState("hinglish");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);

  const abortControllerRef = useRef(null);
  const dropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        try {
          abortControllerRef.current.abort();
        } catch (_e) { }
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Progress animation
  useEffect(() => {
    if (isAnalyzing) {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 500);
      return () => clearInterval(interval);
    } else {
      setProgress(0);
    }
  }, [isAnalyzing]);

  // Handle selected video from leaderboard or search
  useEffect(() => {
    if (selectedVideoId) {
      const videoUrl = `https://www.youtube.com/watch?v=${selectedVideoId}`;
      setYoutubeUrl(videoUrl);
      // Clear previous results
      setAnalysisResult(null);
      setShowResults(false);
      setError(null);
      // Auto-trigger analysis with the URL directly
      setTimeout(() => {
        if (!isAnalyzing) {
          handleAnalysis(videoUrl);
        }
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideoId]);

  // Handle search video selection from navbar
  useEffect(() => {
    const handleSearchVideoSelect = (event) => {
      const url = event.detail?.url;
      if (url && !isAnalyzing) {
        setYoutubeUrl(url);
        // Clear previous results
        setAnalysisResult(null);
        setShowResults(false);
        setError(null);
        // Auto-trigger analysis immediately
        setTimeout(() => {
          if (!isAnalyzing) {
            handleAnalysis(url);
          }
        }, 300);
      }
    };

    window.addEventListener('search-video-selected', handleSearchVideoSelect);
    return () => {
      window.removeEventListener('search-video-selected', handleSearchVideoSelect);
    };
  }, [isAnalyzing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract video ID from URL (supports all YouTube formats)
  const extractVideoId = useCallback((url) => {
    if (!url || typeof url !== "string") return null;
    
    const trimmedUrl = url.trim();
    
    // Patterns to extract video ID from various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/vi\/|youtube\.com\/shorts\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/watch\?.*[&?]v=([a-zA-Z0-9_-]{11})/,
      /m\.youtube\.com\/watch\?.*[&?]v=([a-zA-Z0-9_-]{11})/,
    ];
    
    for (const pattern of patterns) {
      const match = trimmedUrl.match(pattern);
      if (match && match[1] && match[1] !== 'null' && match[1] !== 'undefined') {
        return match[1];
      }
    }
    
    return null;
  }, []);

  // URL validation
  const isValidYouTubeUrl = useCallback((url) => {
    if (!url || typeof url !== "string") return false;
    
    const trimmedUrl = url.trim();
    
    // Check if URL contains null or invalid video ID
    if (trimmedUrl.includes('v=null') || trimmedUrl.includes('v=undefined')) {
      return false;
    }
    
    // Extract video ID to validate
    const videoId = extractVideoId(trimmedUrl);
    if (!videoId || videoId === 'null' || videoId === 'undefined') {
      return false;
    }
    
    // Validate video ID format (should be 11 characters)
    if (videoId.length !== 11) {
      return false;
    }
    
    // Patterns to match various YouTube URL formats
    const patterns = [
      /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/(www\.)?youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/(www\.)?m\.youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/(www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/(www\.)?youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/(www\.)?youtube\.com\/v\/([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/(www\.)?youtube\.com\/vi\/([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/(www\.)?youtube\.com\/playlist\?list=([a-zA-Z0-9_-]+)/, // Playlist support
    ];
    
    return patterns.some((pattern) => pattern.test(trimmedUrl));
  }, [extractVideoId]);

  // Get overall score from analysis result
  const getOverallScore = useCallback(() => {
    if (!analysisResult?.analysis) return 0;
    try {
      if (typeof analysisResult.analysis.gradi_score === 'number') {
        return Math.min(Math.max(analysisResult.analysis.gradi_score, 0), 5);
      }
      if (analysisResult.analysis.ratings) {
        const ratings = analysisResult.analysis.ratings;
        const validScores = Object.values(ratings)
          .filter(rating => rating && typeof rating === 'object' && 'score' in rating)
          .map(rating => {
            const score = parseFloat(rating.score);
            return isNaN(score) ? 0 : Math.min(Math.max(score, 0), 5);
          });
        if (validScores.length === 0) return 0;
        const average = validScores.reduce((sum, score) => sum + score, 0) / validScores.length;
        return Math.round(average * 10) / 10;
      }
      return 0;
    } catch (err) {
      console.error("Error getting overall score:", err);
      return 0;
    }
  }, [analysisResult]);

  // Truncate text to show only first two lines
  const truncateText = useCallback((text, maxChars = 80) => {
    if (!text) return { truncated: "", full: "", needsTruncation: false };
    
    // If text is shorter than maxChars, no truncation needed
    if (text.length <= maxChars) {
      return { truncated: text, full: text, needsTruncation: false };
    }
    
    // Find the last space before maxChars to avoid cutting words
    const truncated = text.substring(0, maxChars);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    const finalTruncated = lastSpaceIndex > maxChars * 0.8 ? truncated.substring(0, lastSpaceIndex) : truncated;
    
    return { truncated: finalTruncated, full: text, needsTruncation: true };
  }, []);

  // Format duration from seconds to readable format
  const formatDuration = useCallback((seconds) => {
    if (!seconds || seconds === 0) return 'N/A';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
  }, []);

  // Handle metric popup
  const handleMetricClick = useCallback((category, data) => {
    setSelectedMetric({ category, data });
    setShowMetricPopup(true);
    
    // Track metric detail view
    ReactGA.event({
      category: 'Engagement',
      action: 'Metric Viewed',
      label: category
    });
    Clarity.event('metric_detail_viewed');
    Clarity.setTag('viewed_metric', category);
  }, []);

  const closeMetricPopup = useCallback(() => {
    setShowMetricPopup(false);
    setSelectedMetric(null);
  }, []);

  const shareResults = async () => {
    if (!analysisResult) return;
    
    setIsSharing(true);
    try {
      const score = getOverallScore().toFixed(1);
      const oneLiner = getScoreMessage();
      
      // Create beautiful shareable card
      const snapshotDiv = document.createElement('div');
      snapshotDiv.style.cssText = `
        width: 1080px;
        height: 1080px;
        background: linear-gradient(135deg, #020204 0%, #1a0a40 50%, #040408 100%);
        color: white;
        font-family: 'Arbutus Slab', serif;
        position: fixed;
        top: -9999px;
        left: -9999px;
        border-radius: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 80px;
        box-sizing: border-box;
        position: relative;
        overflow: hidden;
      `;
      
      snapshotDiv.innerHTML = `
        <!-- Decorative Background Blobs -->
        <div style="position: absolute; top: 10%; left: 10%; width: 400px; height: 400px; background: radial-gradient(circle, rgba(139, 92, 246, 0.3), transparent); border-radius: 50%; filter: blur(80px);"></div>
        <div style="position: absolute; bottom: 10%; right: 10%; width: 500px; height: 500px; background: radial-gradient(circle, rgba(236, 72, 153, 0.3), transparent); border-radius: 50%; filter: blur(100px);"></div>
        
        <!-- Content Container -->
        <div style="position: relative; z-index: 10; text-align: center; width: 100%;">
          
          <!-- Gradi.AI Logo -->
          <h1 style="
            font-size: 5rem;
            font-weight: bold;
            margin: 0 0 3rem 0;
            background: linear-gradient(135deg, #8b5cf6, #ec4899);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            letter-spacing: -2px;
          ">Gradi.AI</h1>
          
          <!-- Score Circle -->
          <div style="
            width: 400px;
            height: 400px;
            margin: 0 auto 4rem;
            border: 4px solid rgba(139, 92, 246, 0.5);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 20px 60px rgba(139, 92, 246, 0.4);
          ">
            <div style="
              font-size: 5.5rem;
              font-weight: 900;
              color: white;
              line-height: 1;
            ">${score}/5.0</div>
          </div>
          
          <!-- One-Liner -->
          <div style="
            background: rgba(15, 15, 35, 0.8);
            border: 2px solid rgba(139, 92, 246, 0.4);
            border-radius: 24px;
            padding: 2.5rem 3rem;
            margin-bottom: 3rem;
            backdrop-filter: blur(20px);
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
          ">
            <p style="
              font-size: 2.2rem;
              line-height: 1.5;
              margin: 0;
              color: #e5e7eb;
              font-weight: 500;
            ">${oneLiner}</p>
          </div>
          
          <!-- Footer -->
          <p style="
            color: rgba(255, 255, 255, 0.5);
            font-size: 1.5rem;
            margin: 0;
            font-weight: 400;
          ">gradi.ai</p>
        </div>
      `;
      
      document.body.appendChild(snapshotDiv);
      
      // Capture screenshot
      const canvas = await html2canvas(snapshotDiv, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      document.body.removeChild(snapshotDiv);
      
      // Convert to blob and share
      canvas.toBlob(async (blob) => {
        try {
          const fileName = 'gradi-score-card.png';
          const shareText = `${oneLiner}\n\nGradi Score: ${score}/5.0 🎯`;
          
          if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([blob], fileName, { type: 'image/png' })] })) {
            await navigator.share({
              title: 'My Gradi Score',
              text: shareText,
              files: [new File([blob], fileName, { type: 'image/png' })]
            });
            
            // Track successful share
            ReactGA.event({
              category: 'Engagement',
              action: 'Share Success',
              label: 'Native Share',
              value: Math.round(score * 10)
            });
            Clarity.event('share_success_native');
          } else {
            // Fallback: download image
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(url);
            
            // Track download fallback
            ReactGA.event({
              category: 'Engagement',
              action: 'Share Download',
              label: 'Download Fallback',
              value: Math.round(score * 10)
            });
            Clarity.event('share_download_fallback');
          }
        } catch (shareErr) {
          // Silent fallback to download
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'gradi-score-card.png';
          a.click();
          URL.revokeObjectURL(url);
          
          // Track error fallback
          ReactGA.event({
            category: 'Engagement',
            action: 'Share Failed',
            label: 'Error Fallback'
          });
          Clarity.event('share_error_fallback');
        }
      }, 'image/png');
      
    } catch (err) {
      // Silent error handling - don't show error to user
    }
    setIsSharing(false);
  };

  // Main analysis function
  const handleAnalysis = async (urlOverride = null) => {
    // Check video analysis limit for Google users
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (currentUser.provider === 'google') {
      // Admin email with unlimited access
      const adminEmails = ['satyarth.prakash@adda247.com'];
      const isAdmin = adminEmails.includes(currentUser.email);
      
      if (!isAdmin) {
        const analysisCount = parseInt(localStorage.getItem(`analysis_count_${currentUser.id}`) || '0');
        if (analysisCount >= 20) {
          setError(`You have reached your limit of 20 video analyses. Please contact your administrator for more access.`);
          return;
        }
      }
    }

    // Handle if event object is passed by mistake
    const urlParam = (urlOverride && typeof urlOverride === 'object' && urlOverride.target) ? null : urlOverride;
    const urlToAnalyze = urlParam || youtubeUrl;
    
    if (!urlToAnalyze || typeof urlToAnalyze !== 'string' || !urlToAnalyze.trim()) {
      setError("Please enter a YouTube URL");
      return;
    }
    
    const trimmedUrl = urlToAnalyze.trim();
    
    // Check for null/undefined in URL
    if (trimmedUrl.includes('v=null') || trimmedUrl.includes('v=undefined') || trimmedUrl.includes('watch?v=null')) {
      setError("Invalid video URL. Please try searching again or paste a valid YouTube URL.");
      console.error("❌ URL contains null/undefined:", trimmedUrl);
      return;
    }
    
    // Extract and validate video ID
    const videoId = extractVideoId(trimmedUrl);
    if (!videoId || videoId === 'null' || videoId === 'undefined') {
      setError("Could not extract video ID from URL. Please check the URL and try again.");
      console.error("❌ Failed to extract video ID from URL:", trimmedUrl);
      return;
    }
    
    if (!isValidYouTubeUrl(trimmedUrl)) {
      // Debug: Log the URL and validation result
      console.error("❌ URL Validation Failed:", trimmedUrl);
      console.error("Extracted video ID:", videoId);
      console.error("URL type:", typeof trimmedUrl);
      console.error("URL length:", trimmedUrl.length);
      setError(`Please enter a valid YouTube URL. Supported formats: youtube.com/watch?v=..., youtu.be/..., youtube.com/shorts/...`);
      return;
    }
    
    // Debug: Log successful validation
    console.log("✅ URL Validation Passed:", urlToAnalyze);

    setIsAnalyzing(true);
    setAnalysisResult(null);
    setError(null);
    setShowResults(false);
    setAnalysisStage("Preparing request...");
    setProgress(10);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setAnalysisStage("📄 Fetching transcript from Dumpling AI...");
      setProgress(30);
      await new Promise((r) => setTimeout(r, 800));

      setAnalysisStage("Gradi is analyzing...");
      setProgress(60);
      await new Promise((r) => setTimeout(r, 800));

      // Check proxy status before analysis
      console.log("🔍 Checking proxy status...");
      try {
        const proxyStatus = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/health`);
        const proxyData = await proxyStatus.json();
        console.log("🌐 Proxy Status:", {
          configured: proxyData.proxy?.configured,
          count: proxyData.proxy?.proxy_count,
          maxRetries: proxyData.proxy?.max_retries,
          retryDelay: proxyData.proxy?.retry_delay
        });
        
        if (!proxyData.proxy?.configured) {
          console.warn("⚠️ No proxy configured in production - YouTube may block requests");
        } else {
          console.log("✅ Proxy configured with", proxyData.proxy.proxy_count, "proxies");
        }
      } catch (proxyError) {
        console.warn("⚠️ Could not check proxy status:", proxyError);
      }

      // Track analysis start in GA4
      ReactGA.event({
        category: 'Video Analysis',
        action: 'Analysis Started',
        label: analysisLanguage
      });

      // Track analysis start in Clarity
      Clarity.event('video_analysis_started');
      Clarity.setTag('analysis_language', analysisLanguage);

      const response = await apiService.analyzeVideo(urlToAnalyze, { 
        signal: controller.signal,
        analysisLanguage: analysisLanguage,
        category: selectedCategory
      });

      if (!response) throw new Error("No response from analysis service");
      if (response.success === false) {
        // Handle transcript-specific errors with better user guidance
        const errorMessage = response.error || "Analysis failed on server";
        
        console.log("❌ Analysis failed:", errorMessage);
        
        if (errorMessage.includes("No transcript available") || errorMessage.includes("transcript")) {
          // Extract the helpful error message from backend
          const lines = errorMessage.split('\n');
          const mainError = lines[0];
          const suggestions = lines.slice(1).filter(line => line.trim().startsWith('•'));
          
          console.log("📝 Transcript error details:", {
            main: mainError,
            suggestions: suggestions,
            type: 'transcript_error'
          });
          
          throw new Error({
            main: mainError,
            suggestions: suggestions,
            type: 'transcript_error'
          });
        }
        
        throw new Error(errorMessage);
      }

      if (response && response.success && response.data) {
        console.log("✅ Analysis successful:", {
          hasAnalysis: !!response.data.analysis,
          hasTranscript: !!response.data.transcript,
          analysisLength: response.data.analysis?.length || 0
        });
        setProgress(100);
        setAnalysisStage("Analysis completed!");
        await new Promise((r) => setTimeout(r, 500));
        setAnalysisResult(response.data);
        setShowResults(true);
        
        // Save to history
        saveToHistory(currentUser, response.data);
        
        // Increment analysis count for Google users (skip for admins)
        if (currentUser.provider === 'google') {
          const adminEmails = ['satyarth.prakash@adda247.com'];
          const isAdmin = adminEmails.includes(currentUser.email);
          
          if (!isAdmin) {
            const currentCount = parseInt(localStorage.getItem(`analysis_count_${currentUser.id}`) || '0');
            const newCount = currentCount + 1;
            localStorage.setItem(`analysis_count_${currentUser.id}`, newCount.toString());
            console.log(`📊 Analysis count for ${currentUser.email}: ${newCount}/20`);
          } else {
            console.log(`✅ Admin user ${currentUser.email} - unlimited access (no count increment)`);
          }
        }
        
        // Track successful analysis in GA4
        const score = response.data.analysis?.gradi_score?.score;
        ReactGA.event({
          category: 'Video Analysis',
          action: 'Analysis Completed',
          label: analysisLanguage,
          value: score ? Math.round(score * 10) : undefined
        });
        
        // Track score range for analytics
        if (score) {
          let scoreRange = 'Unknown';
          if (score >= 4.5) scoreRange = 'Excellent (4.5-5.0)';
          else if (score >= 4.0) scoreRange = 'Very Good (4.0-4.4)';
          else if (score >= 3.5) scoreRange = 'Good (3.5-3.9)';
          else if (score >= 3.0) scoreRange = 'Average (3.0-3.4)';
          else if (score >= 2.0) scoreRange = 'Below Average (2.0-2.9)';
          else scoreRange = 'Poor (0-1.9)';
          
          ReactGA.event({
            category: 'Video Quality',
            action: 'Score Range',
            label: scoreRange,
            value: Math.round(score * 10)
          });
          Clarity.setTag('score_range', scoreRange);
        }

        // Track successful analysis in Clarity
        Clarity.event('video_analysis_completed');
        if (score) {
          Clarity.setTag('analysis_score', score.toString());
        }
        
        // Pass analysis result to App for Mind Map
        if (onAnalysisComplete) {
          onAnalysisComplete(response.data);
        }

        // Track user activity
        if (currentUser && currentUser.email) {
          const videoId = response.data.videoId || extractVideoId(youtubeUrl);
          const videoTitle = response.data.videoTitle || response.data.analysis?.video_metadata?.title || 'Unknown';
          
          apiService.trackActivity({
            user_email: currentUser.email,
            user_id: currentUser.id,
            username: currentUser.name || currentUser.email,
            action: 'video_analyzed',
            video_id: videoId,
            video_url: youtubeUrl,
            video_title: videoTitle,
            timestamp: new Date().toISOString(),
            metadata: {
              language: analysisLanguage,
              category: selectedCategory,
              score: response.data.analysis?.gradi_score?.score
            }
          }).catch(err => {
            console.error('Failed to track activity:', err);
            // Don't show error to user - activity tracking is non-critical
          });
        }
      } else {
        console.error("❌ Invalid response structure:", response);
        throw new Error("Invalid response structure");
      }

    } catch (err) {
      if (err.name === "AbortError") {
        setError("Analysis aborted.");
        
        // Track abort in GA4
        ReactGA.event({
          category: 'Video Analysis',
          action: 'Analysis Aborted',
          label: analysisLanguage
        });
        
        Clarity.event('video_analysis_aborted');
      } else {
        console.error("Analysis error:", err);
        
        // Track analysis errors in GA4
        ReactGA.event({
          category: 'Video Analysis',
          action: 'Analysis Failed',
          label: err?.message || 'unknown_error'
        });
        
        // Track analysis errors in Clarity
        Clarity.event('video_analysis_failed');
        Clarity.setTag('error_type', err?.message || 'unknown_error');
        
        // Handle structured transcript errors
        if (typeof err?.message === 'object' && err.message.type === 'transcript_error') {
          const { main, suggestions } = err.message;
          setError(`🎯 ${main}\n\nPlease try:\n${suggestions.join('\n')}\n\n💡 Tip: Make sure the video has subtitles enabled or try a different video.`);
        } else {
          const friendly = err?.message || "An unexpected error occurred";
          setError(friendly);
        }
      }
    } finally {
      setIsAnalyzing(false);
      setAnalysisStage("");
      setProgress(0);
      abortControllerRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !isAnalyzing) {
      handleAnalysis();
    }
  };

  const getScoreMessage = () => {
    const score = getOverallScore();
    
    // Try to get dynamic one-liner from AI analysis
    if (analysisResult?.analysis?.one_liner) {
      return analysisResult.analysis.one_liner;
    }
    
    // Fallback messages if one-liner not available
    if (score >= 4.5) return "Outstanding educational content! 🌟";
    if (score >= 4) return "Excellent teaching quality! 🔥";
    if (score >= 3.5) return "Very good content with minor improvements needed 👍";
    if (score >= 3) return "Good content, some areas can be enhanced 📚";
    if (score >= 2.5) return "Decent effort, needs more work 💪";
    if (score >= 2) return "Basic content, requires significant improvement 📝";
    return "Needs major improvements for better learning outcomes 🎯";
  };

  // Generate floating particles
  const generateParticles = () => {
    const particles = [];
    const colors = ['#8b5cf6', '#ec4899', '#06b6d4', '#10b981'];
    
    for (let i = 0; i < 50; i++) {
      particles.push(
        <div
          key={i}
          className="particle"
          style={{
            width: Math.random() * 4 + 2 + 'px',
            height: Math.random() * 4 + 2 + 'px',
            background: `linear-gradient(45deg, ${colors[Math.floor(Math.random() * 4)]}, transparent)`,
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
            animationDelay: Math.random() * 5 + 's'
          }}
        />
      );
    }
    return particles;
  };

  return (
    <div className="page-container">
      {/* Animated Background Elements */}
      <div className="animated-background" />

      {/* Robot background image at bottom - only on home page */}
      <div className="robot-background" />

      {/* Floating particles */}
      <div className="floating-particles">
        {generateParticles()}
      </div>

      <div className="page-content">
        {/* Hero Section */}
        <div className="hero-section">
          {/* Main Heading */}
          <h1 className="hero-title">
            Gradi.AI
          </h1>

          {/* Subheading */}
          <p className="hero-subtitle">
            Enlighten. Educate. Elevate
          </p>

          {/* Description */}
          <p className="hero-description">
            Paste Your Video Link & Get a Detailed Analysis
          </p>

          {/* Input Section */}
          <div className="input-section">
            <div className={`input-container ${showResults ? 'with-results' : ''}`}>
              <input
                type="text"
                placeholder="Paste Your Link Here"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  // Track URL input focus
                  ReactGA.event({
                    category: 'Engagement',
                    action: 'URL Input Focus',
                    label: 'Input Interaction'
                  });
                }}
                onPaste={() => {
                  // Track paste action
                  ReactGA.event({
                    category: 'Engagement',
                    action: 'URL Pasted',
                    label: 'Paste Action'
                  });
                  Clarity.event('url_pasted');
                }}
                disabled={isAnalyzing}
                className="input-field"
              />
              <div ref={dropdownRef} className="custom-dropdown" onClick={() => !isAnalyzing && setIsDropdownOpen(!isDropdownOpen)}>
                <div className={`language-select ${isDropdownOpen ? 'open' : ''} ${isAnalyzing ? 'disabled' : ''}`}>
                  <span className="dropdown-text">
                    {analysisLanguage === "english" ? "English Analysis" : "Hinglish Analysis"}
                  </span>
                  <span className={`dropdown-arrow ${isDropdownOpen ? 'rotated' : ''}`}>
                    ▾
                  </span>
                </div>
                {isDropdownOpen && !isAnalyzing && (
                  <div className="dropdown-options">
                    <div 
                      className={`dropdown-option ${analysisLanguage === 'hinglish' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (analysisLanguage !== 'hinglish') {
                          setAnalysisLanguage('hinglish');
                          // Track language change
                          ReactGA.event({
                            category: 'Settings',
                            action: 'Language Changed',
                            label: 'Hinglish'
                          });
                          Clarity.event('language_changed_hinglish');
                        }
                        setIsDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">Hinglish Analysis</span>
                      {analysisLanguage === 'hinglish' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${analysisLanguage === 'english' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (analysisLanguage !== 'english') {
                          setAnalysisLanguage('english');
                          // Track language change
                          ReactGA.event({
                            category: 'Settings',
                            action: 'Language Changed',
                            label: 'English'
                          });
                          Clarity.event('language_changed_english');
                        }
                        setIsDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">English Analysis</span>
                      {analysisLanguage === 'english' && <span className="check-icon">●</span>}
                    </div>
                  </div>
                )}
              </div>
              <div ref={categoryDropdownRef} className="custom-dropdown" onClick={() => !isAnalyzing && setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}>
                <div className={`category-select ${isCategoryDropdownOpen ? 'open' : ''} ${isAnalyzing ? 'disabled' : ''}`}>
                  <span className="dropdown-text">
                    {selectedCategory || "Auto-Detect Category"}
                  </span>
                  <span className={`dropdown-arrow ${isCategoryDropdownOpen ? 'rotated' : ''}`}>
                    ▾
                  </span>
                </div>
                {isCategoryDropdownOpen && !isAnalyzing && (
                  <div className="dropdown-options">
                    <div 
                      className={`dropdown-option ${selectedCategory === null ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory(null);
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">Auto-Detect Category</span>
                      {selectedCategory === null && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'Banking' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('Banking');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">Banking</span>
                      {selectedCategory === 'Banking' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'SSC' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('SSC');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">SSC</span>
                      {selectedCategory === 'SSC' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'Railway' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('Railway');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">Railway</span>
                      {selectedCategory === 'Railway' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'Teaching' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('Teaching');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">Teaching</span>
                      {selectedCategory === 'Teaching' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'Defence' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('Defence');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">Defence</span>
                      {selectedCategory === 'Defence' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'State Exams' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('State Exams');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">State Exams</span>
                      {selectedCategory === 'State Exams' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'General Studies' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('General Studies');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">General Studies</span>
                      {selectedCategory === 'General Studies' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'General' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('General');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">General</span>
                      {selectedCategory === 'General' && <span className="check-icon">●</span>}
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => handleAnalysis()}
                disabled={isAnalyzing || !youtubeUrl.trim()}
                className="analyze-button"
              >
                {isAnalyzing ? (
                  <>
                    <span className="spinner" />
                    Analyzing...
                  </>
                ) : (
                  'Analyze'
                )}
              </button>
            </div>

            {/* Progress Section */}
            {isAnalyzing && analysisStage && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(15px)',
                animation: 'fadeIn 0.5s ease-out'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(15, 15, 35, 0.95), rgba(30, 30, 60, 0.95))',
                  border: '2px solid rgba(139, 92, 246, 0.4)',
                  borderRadius: '32px',
                  padding: '3rem',
                  maxWidth: '500px',
                  width: '90%',
                  textAlign: 'center',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
                  animation: 'slideIn 0.5s ease-out',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Animated Background Elements */}
                  <div style={{
                    position: 'absolute',
                    top: '-50%',
                    right: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
                    animation: 'rotate 20s linear infinite'
                  }} />
                  
                  {/* Professional Icon */}
                  <div style={{
                    fontSize: '3rem',
                    marginBottom: '2rem',
                    animation: 'float 4s ease-in-out infinite',
                    filter: 'drop-shadow(0 0 20px rgba(139, 92, 246, 0.5))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      border: '3px solid #8b5cf6',
                      borderTop: '3px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 2s linear infinite'
                    }} />
                  </div>

                  {/* Main Text */}
                  <h2 style={{
                    fontSize: '1.8rem',
                    fontWeight: '800',
                    color: '#e5e7eb',
                    marginBottom: '1rem',
                    background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: 'glow 3s ease-in-out infinite alternate'
                  }}>
                    Gradi is analyzing...
                  </h2>

                  {/* Progress Bar */}
                  <div style={{
                    width: '100%',
                    height: '12px',
                    background: 'rgba(30, 30, 60, 0.8)',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    marginBottom: '1rem',
                    position: 'relative'
                  }}>
                    <div style={{
                      width: `${progress}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #8b5cf6, #ec4899, #06b6d4)',
                      borderRadius: '10px',
                      transition: 'width 0.5s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}>
                      {/* Shimmer Effect */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                        animation: 'shimmer 2s infinite'
                      }} />
                    </div>
                </div>

                  {/* Percentage */}
                  <div style={{
                    color: '#06b6d4',
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    marginBottom: '2rem'
                  }}>
                  {Math.round(progress)}% Complete
                  </div>

                  {/* Floating Particles */}
                  <div style={{
                    position: 'absolute',
                    top: '20%',
                    left: '10%',
                    width: '6px',
                    height: '6px',
                    background: '#8b5cf6',
                    borderRadius: '50%',
                    animation: 'floatParticle 6s ease-in-out infinite',
                    opacity: 0.4
                  }} />
                  <div style={{
                    position: 'absolute',
                    top: '60%',
                    right: '15%',
                    width: '4px',
                    height: '4px',
                    background: '#ec4899',
                    borderRadius: '50%',
                    animation: 'floatParticle 5s ease-in-out infinite 1s',
                    opacity: 0.4
                  }} />
                  <div style={{
                    position: 'absolute',
                    bottom: '30%',
                    left: '20%',
                    width: '8px',
                    height: '8px',
                    background: '#06b6d4',
                    borderRadius: '50%',
                    animation: 'floatParticle 7s ease-in-out infinite 2s',
                    opacity: 0.4
                  }} />
                </div>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="error-container">
                <p className="error-content">
                  {error}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Results Section */}
        {analysisResult && (
          <div id="analysis-report" className="results-container">
            {/* Score Display */}
            <div className="score-display">
              <div className="score-value">
                {getOverallScore().toFixed(1)}/5.0
              </div>
              <p className="score-message">
                {getScoreMessage()}
              </p>
            </div>

            {/* Video Analytics */}
            {analysisResult.video_metadata && (
              <div style={{
                marginBottom: '3rem'
              }}>
                <h3 style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  marginBottom: '1.5rem',
                  background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Video Analytics
                </h3>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '1.5rem',
                  marginBottom: '2rem'
                }}>
                  {/* Views */}
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.05))',
                    border: '2px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    textAlign: 'center',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: '#8b5cf6',
                      marginBottom: '0.5rem'
                    }}>
                      {analysisResult.video_metadata.view_count ? 
                        new Intl.NumberFormat().format(analysisResult.video_metadata.view_count) : 
                        'N/A'
                      }
                    </div>
                    <div style={{
                      fontSize: '0.9rem',
                      color: '#9ca3af',
                      fontWeight: '500'
                    }}>Views</div>
                  </div>

                  {/* Likes */}
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.05))',
                    border: '2px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    textAlign: 'center',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: '#8b5cf6',
                      marginBottom: '0.5rem'
                    }}>
                      {analysisResult.video_metadata.like_count ? 
                        new Intl.NumberFormat().format(analysisResult.video_metadata.like_count) : 
                        'N/A'
                      }
                    </div>
                    <div style={{
                      fontSize: '0.9rem',
                      color: '#9ca3af',
                      fontWeight: '500'
                    }}>Likes</div>
                  </div>

                  {/* Duration */}
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.05))',
                    border: '2px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '16px',
                    padding: '1.5rem',
                    textAlign: 'center',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: '#8b5cf6',
                      marginBottom: '0.5rem'
                    }}>
                      {analysisResult.video_metadata.duration ? 
                        formatDuration(analysisResult.video_metadata.duration) : 
                        'N/A'
                      }
                    </div>
                    <div style={{
                      fontSize: '0.9rem',
                      color: '#9ca3af',
                      fontWeight: '500'
                    }}>Duration</div>
                  </div>
                </div>

                {/* Video Info */}
                <div style={{
                  background: 'rgba(15, 15, 35, 0.9)',
                  border: '2px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '16px',
                  padding: '1.5rem',
                  backdropFilter: 'blur(10px)'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '1.5rem'
                  }}>
                    {/* Video Title */}
                    <div>
                      <h4 style={{
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        color: '#8b5cf6',
                        marginBottom: '0.5rem'
                      }}>Video Title</h4>
                      <p style={{
                        color: '#e5e7eb',
                        fontSize: '1rem',
                        lineHeight: '1.5'
                      }}>
                        {analysisResult.video_metadata.title || 'N/A'}
                      </p>
                    </div>

                    {/* Channel */}
                    <div>
                      <h4 style={{
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        color: '#8b5cf6',
                        marginBottom: '0.5rem'
                      }}>Channel</h4>
                      <p style={{
                        color: '#e5e7eb',
                        fontSize: '1rem',
                        lineHeight: '1.5'
                      }}>
                        {analysisResult.video_metadata.channel || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Summary */}
            {analysisResult.analysis?.summary && (
              <div style={{
                marginBottom: '3rem'
              }}>
                <h3 style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  marginBottom: '1.5rem',
                  background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Video Summary
                </h3>
                <p style={{
                  color: '#e5e7eb',
                  lineHeight: '1.8',
                  fontSize: '1.1rem'
                }}>
                  {analysisResult.analysis.summary}
                </p>
              </div>
            )}

            {/* Positives */}
            {analysisResult.analysis?.positives && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
                gap: '2rem',
                marginBottom: '3rem'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.05))',
                  border: '2px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '24px',
                  padding: '2.5rem',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
                }}>
                  <h3 style={{
                    color: '#8b5cf6',
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    marginBottom: '1.5rem',
                    textAlign: 'center'
                  }}>
                    Positives - Jo Gradi Ko Pasand Aaya
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {analysisResult.analysis.positives.map((positive, idx) => (
                      <li key={idx} style={{
                        color: '#e5e7eb',
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                        fontSize: '1rem',
                        lineHeight: '1.6'
                      }}>
                        <span style={{
                          color: '#8b5cf6',
                          fontSize: '1.2rem',
                          minWidth: '1.2rem'
                        }}>•</span>
                        <span>{positive}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Areas of Improvements - Merged from negatives and suggestions */}
            {((analysisResult.analysis?.areas_of_improvement && analysisResult.analysis.areas_of_improvement.length > 0) ||
              (analysisResult.analysis?.negatives && analysisResult.analysis.negatives.length > 0) ||
              (analysisResult.analysis?.suggestions_for_improvement && analysisResult.analysis.suggestions_for_improvement.length > 0)) && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.05))',
                border: '2px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '24px',
                padding: '2.5rem',
                marginBottom: '3rem',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)'
              }}>
                <h3 style={{
                  color: '#8b5cf6',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}>
                  Areas of Improvements
                </h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {/* Use areas_of_improvement if available, otherwise merge negatives and suggestions */}
                  {(analysisResult.analysis?.areas_of_improvement || []).length > 0 ? (
                    analysisResult.analysis.areas_of_improvement.map((item, idx) => (
                      <li key={idx} style={{
                        color: '#e5e7eb',
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                        fontSize: '1rem',
                        lineHeight: '1.6'
                      }}>
                        <span style={{
                          color: '#8b5cf6',
                          fontSize: '1.2rem',
                          minWidth: '1.2rem'
                        }}>•</span>
                        <span>{item}</span>
                      </li>
                    ))
                  ) : (
                    <>
                      {(analysisResult.analysis?.negatives || []).map((negative, idx) => (
                        <li key={`neg-${idx}`} style={{
                          color: '#e5e7eb',
                          marginBottom: '1rem',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.75rem',
                          fontSize: '1rem',
                          lineHeight: '1.6'
                        }}>
                          <span style={{
                            color: '#8b5cf6',
                            fontSize: '1.2rem',
                            minWidth: '1.2rem'
                          }}>•</span>
                          <span>{negative}</span>
                        </li>
                      ))}
                      {(analysisResult.analysis?.suggestions_for_improvement || []).map((suggestion, idx) => (
                        <li key={`sug-${idx}`} style={{
                          color: '#e5e7eb',
                          marginBottom: '1rem',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.75rem',
                          fontSize: '1rem',
                          lineHeight: '1.6'
                        }}>
                          <span style={{
                            color: '#8b5cf6',
                            fontSize: '1.2rem',
                            minWidth: '1.2rem'
                          }}>•</span>
                          <span>{suggestion}</span>
                        </li>
                      ))}
                    </>
                  )}
                </ul>
              </div>
            )}

            {/* Detailed Ratings */}
            {analysisResult.analysis?.ratings && (
              <div>
                <h2 style={{
                  fontSize: '2rem',
                  fontWeight: '800',
                  marginBottom: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textAlign: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{
                    fontSize: '1.5rem',
                    filter: 'drop-shadow(0 0 15px rgba(139, 92, 246, 0.7))'
                  }}>🎯</span>
                  Detailed Category Ratings
                </h2>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                  gap: '2rem'
                }}>
                  {Object.entries(analysisResult.analysis.ratings).map(([category, data], idx) => (
                    <div key={category} className="metric-card" style={{
                      background: 'rgba(15, 15, 35, 0.9)',
                      border: '2px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '24px',
                      padding: '2.5rem',
                      backdropFilter: 'blur(20px)',
                      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
                      transition: 'all 0.3s ease',
                      maxHeight: '400px',
                      overflow: 'auto',
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'rgba(139, 92, 246, 0.4) rgba(15, 15, 35, 0.1)'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '1.5rem',
                        flexWrap: 'wrap',
                        gap: '1rem'
                      }}>
                        <h3 style={{
                          fontSize: '1.3rem',
                          fontWeight: '700',
                          color: '#e5e7eb',
                          margin: 0,
                          flex: 1,
                          minWidth: '200px'
                        }}>
                          {category}
                        </h3>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1rem',
                          flexWrap: 'wrap'
                        }}>
                          <span style={{
                            color: '#8b5cf6',
                            fontSize: '1.8rem',
                            fontWeight: '900'
                          }}>
                            {typeof data === "object" ? data.score?.toFixed(1) : data}/5
                          </span>
                          <span style={{
                            color: typeof data === "object" && data.score >= 4 ? '#10b981' :
                              typeof data === "object" && data.score >= 3 ? '#f59e0b' : '#ef4444',
                            fontWeight: '700',
                            fontSize: '1rem'
                          }}>
                            {typeof data === "object" && data.score >= 4 ? "Great! ⭐" :
                              typeof data === "object" && data.score >= 3 ? "Good 👍" : "Needs Work 💪"}
                          </span>
                        </div>
                      </div>

                      {/* Progress Bar for Score */}
                      <div style={{
                        width: '100%',
                        height: '8px',
                        background: 'rgba(30, 30, 60, 0.8)',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        marginBottom: '1.5rem'
                      }}>
                        <div style={{
                          width: `${typeof data === "object" ? (data.score / 5) * 100 : (data / 5) * 100}%`,
                          height: '100%',
                          background: typeof data === "object" && data.score >= 4
                            ? 'linear-gradient(90deg, #10b981, #34d399)'
                            : typeof data === "object" && data.score >= 3
                              ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                              : 'linear-gradient(90deg, #ef4444, #f87171)',
                          borderRadius: '10px'
                        }} />
                      </div>

                      {typeof data === "object" && data.reason && (
                        <div>
                        <p style={{
                          color: '#d1d5db',
                          lineHeight: '1.7',
                          fontSize: '1rem',
                          margin: 0,
                            fontWeight: '400',
                            whiteSpace: 'pre-line'
                          }}>
                            {truncateText(data.reason).truncated}
                          </p>
                          {(truncateText(data.reason).needsTruncation || data.reason.length > 50) && (
                            <button
                              onClick={() => handleMetricClick(category, data)}
                              style={{
                                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(236, 72, 153, 0.1))',
                                border: '1px solid rgba(139, 92, 246, 0.4)',
                                borderRadius: '12px',
                                padding: '0.5rem 1rem',
                                color: '#8b5cf6',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                marginTop: '1rem',
                                transition: 'all 0.3s ease',
                                backdropFilter: 'blur(10px)'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.2))';
                                e.target.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(236, 72, 153, 0.1))';
                                e.target.style.transform = 'translateY(0)';
                              }}
                            >
                              ... Click for more
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Metric Popup Modal */}
        {showMetricPopup && selectedMetric && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(20px)',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(15, 15, 35, 0.95), rgba(30, 30, 60, 0.95))',
              border: '2px solid rgba(139, 92, 246, 0.4)',
              borderRadius: '24px',
              padding: '2.5rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
              animation: 'slideIn 0.3s ease-out',
              position: 'relative'
            }}>
              {/* Close Button */}
              <button
                onClick={closeMetricPopup}
                style={{
                  position: 'absolute',
                  top: '1.5rem',
                  right: '1.5rem',
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  color: '#ef4444',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(10px)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.3)';
                  e.target.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                ×
              </button>

              {/* Header */}
              <h2 style={{
                fontSize: '1.8rem',
                fontWeight: '800',
                color: '#e5e7eb',
                marginBottom: '2rem',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {selectedMetric.category}
              </h2>

              {/* Score Display */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1rem',
                marginBottom: '2rem',
                padding: '1rem',
                background: 'rgba(139, 92, 246, 0.1)',
                borderRadius: '16px',
                border: '1px solid rgba(139, 92, 246, 0.3)'
              }}>
                <span style={{
                  color: '#8b5cf6',
                  fontSize: '2.5rem',
                  fontWeight: '900'
                }}>
                  {typeof selectedMetric.data === "object" ? selectedMetric.data.score?.toFixed(1) : selectedMetric.data}/5
                </span>
                <span style={{
                  color: typeof selectedMetric.data === "object" && selectedMetric.data.score >= 4 ? '#10b981' :
                    typeof selectedMetric.data === "object" && selectedMetric.data.score >= 3 ? '#f59e0b' : '#ef4444',
                  fontWeight: '700',
                  fontSize: '1.2rem'
                }}>
                  {typeof selectedMetric.data === "object" && selectedMetric.data.score >= 4 ? "Great! ⭐" :
                    typeof selectedMetric.data === "object" && selectedMetric.data.score >= 3 ? "Good 👍" : "Needs Work 💪"}
                </span>
              </div>

              {/* Full Content */}
              {typeof selectedMetric.data === "object" && selectedMetric.data.reason && (
                <div style={{
                  background: 'rgba(15, 15, 35, 0.5)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  borderRadius: '16px',
                  padding: '1.5rem',
                  marginBottom: '2rem'
                }}>
                  <h3 style={{
                    color: '#8b5cf6',
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    marginBottom: '1rem',
                    textAlign: 'center'
                  }}>
                    Detailed Analysis
                  </h3>
                  <p style={{
                    color: '#d1d5db',
                    lineHeight: '1.8',
                    fontSize: '1rem',
                    margin: 0,
                    fontWeight: '400',
                    whiteSpace: 'pre-line'
                  }}>
                    {selectedMetric.data.reason}
                  </p>
                </div>
              )}

            </div>
          </div>
        )}
      </div>

      {/* Share Button */}
      {analysisResult && (
        <button
          onClick={shareResults}
          disabled={isSharing}
          className="share-button"
          title="Share Results"
        >
          {isSharing ? '⏳' : '📤'}
        </button>
      )}
    </div>
  );
};

export default HomePage;